import type { ReactNode, Ref } from "react";
import { Box, type BoxProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { useChatViewport } from "./useChatViewport";

type TranscriptProps = ReturnType<typeof useChatViewport>["transcriptProps"];
type DataAttributes = { [key: `data-${string}`]: string | undefined };
type SxEntry = Extract<SxProps<Theme>, readonly unknown[]>[number];
const sxEntries = (sx?: SxProps<Theme>): readonly SxEntry[] =>
  Array.isArray(sx) ? (sx as readonly SxEntry[]) : sx ? [sx as SxEntry] : [];

interface NikolaChatSurfaceProps {
  variant: "page" | "embedded";
  transcriptProps: TranscriptProps;
  composerRef: Ref<HTMLDivElement>;
  threadProps?: Omit<BoxProps, "children" | "ref" | "sx"> & DataAttributes;
  threadSx?: SxProps<Theme>;
  composerProps?: Omit<BoxProps, "children" | "ref" | "sx"> & DataAttributes;
  composerSx?: SxProps<Theme>;
  children: ReactNode;
  afterThread?: ReactNode;
  beforeComposer?: ReactNode;
  composer: ReactNode;
}

export const NikolaChatSurface = ({
  variant,
  transcriptProps,
  composerRef,
  threadProps,
  threadSx,
  composerProps,
  composerSx,
  children,
  afterThread,
  beforeComposer,
  composer,
}: NikolaChatSurfaceProps) => (
  <>
    <Box
      {...threadProps}
      {...transcriptProps}
      sx={[
        {
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          ...(variant === "embedded"
            ? { overscrollBehaviorY: "contain", WebkitOverflowScrolling: "touch", px: 1, pt: 1 }
            : { px: { xs: 1.25, sm: 2.5 }, pt: 2.5 }),
        },
        ...sxEntries(threadSx),
      ]}
    >
      {children}
    </Box>
    {afterThread}
    <Box
      {...composerProps}
      ref={composerRef}
      sx={[{ position: "sticky", bottom: 0, zIndex: 3, bgcolor: "background.default" }, ...sxEntries(composerSx)]}
    >
      {beforeComposer}
      {composer}
    </Box>
  </>
);
